import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    debounce: { type: Number, default: 500 },
    loadingTarget: String
  }

  connect() {
    this.debounceTimer = null
    this.isSubmitting = false
    this.autoFilterEnabled = true
    
    // Get loading indicator if specified
    if (this.loadingTargetValue) {
      this.loadingElement = document.querySelector(this.loadingTargetValue)
    }
    
    // Get manual apply button
    this.manualApplyBtn = document.getElementById('manual-apply-btn')
    
    // Add keyboard navigation support
    this.element.addEventListener('keydown', this.handleKeydown.bind(this))
    
    // Add accessibility enhancements
    this.setupAccessibility()
    
    // Initialize UI state
    this.updateUIState()
  }

  disconnect() {
    this.clearDebounce()
    this.element.removeEventListener('keydown', this.handleKeydown.bind(this))
  }

  // Auto-submit form on filter changes (only for search and sort, not multi-select dropdowns)
  submit(event) {
    if (this.isSubmitting || !this.autoFilterEnabled) return
    
    // Check if this is from a multi-select dropdown - if so, don't auto-submit
    const target = event?.target || event?.currentTarget
    if (target && this.isMultiSelectInput(target)) {
      // Let the manual filter controller handle this
      return
    }
    
    this.clearDebounce()
    this.showLoading()
    this.isSubmitting = true
    
    // Small delay to prevent rapid-fire submissions
    setTimeout(() => {
      try {
        this.element.submit()
        this.announceFilterChange('Filters updated')
      } catch (error) {
        console.error('Filter submission error:', error)
        this.hideLoading()
        this.showError('Failed to apply filters. Please try again.')
      }
    }, 50)
  }

  // Debounced submit for search input
  debounceSubmit(event) {
    if (this.isSubmitting || !this.autoFilterEnabled) return
    
    this.clearDebounce()
    
    this.debounceTimer = setTimeout(() => {
      try {
        this.showLoading()
        this.isSubmitting = true
        this.element.submit()
        this.announceFilterChange('Search updated')
      } catch (error) {
        console.error('Search submission error:', error)
        this.hideLoading()
        this.showError('Failed to update search. Please try again.')
      }
    }, this.debounceValue)
  }

  // Clear individual filter tags
  removeFilter(event) {
    event.preventDefault()
    if (this.isSubmitting) return
    
    const param = event.currentTarget.dataset.autoFilterParam
    const input = this.element.querySelector(`[name="${param}"]`)
    
    if (input) {
      if (input.tagName === 'SELECT') {
        input.selectedIndex = 0 // Reset to first option (usually "All")
      } else {
        input.value = ''
      }
      
      this.submit(event)
    }
  }

  // Clear search field
  clearSearch(event) {
    event.preventDefault()
    if (this.isSubmitting) return
    
    const searchInput = this.element.querySelector('input[name="search"]')
    if (searchInput) {
      searchInput.value = ''
      this.submit(event)
    }
  }

  // Clear date range filters
  clearDateRange(event) {
    event.preventDefault()
    if (this.isSubmitting) return
    
    const dateFromInput = this.element.querySelector('input[name="date_from"]')
    const dateToInput = this.element.querySelector('input[name="date_to"]')
    
    if (dateFromInput) dateFromInput.value = ''
    if (dateToInput) dateToInput.value = ''
    
    this.submit(event)
  }

  // Clear amount range filters
  clearAmountRange(event) {
    event.preventDefault()
    if (this.isSubmitting) return
    
    const minAmountInput = this.element.querySelector('input[name="min_amount"]')
    const maxAmountInput = this.element.querySelector('input[name="max_amount"]')
    
    if (minAmountInput) minAmountInput.value = ''
    if (maxAmountInput) maxAmountInput.value = ''
    
    this.submit(event)
  }

  // Private methods
  clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  // Check if input is part of a multi-select dropdown (should use manual apply)
  isMultiSelectInput(target) {
    // Check if the input is inside a multi-select component
    const multiSelectContainer = target.closest('[data-controller*="multi-select"]')
    if (multiSelectContainer) return true
    
    // Check if it's a checkbox with a name ending in [] (multi-select)
    if (target.type === 'checkbox' && target.name.endsWith('[]')) return true
    
    // Check if it's specifically the role, status, or application_filter fields (known multi-select)
    const multiSelectFields = ['role[]', 'status[]', 'application_filter[]']
    if (multiSelectFields.includes(target.name)) return true
    
    return false
  }

  showLoading() {
    // Show desktop loading indicator
    if (this.loadingElement) {
      this.loadingElement.classList.remove('hidden')
    }
    
    // Show mobile loading indicator
    const mobileLoadingElement = document.querySelector(`${this.loadingTargetValue}-mobile`)
    if (mobileLoadingElement) {
      mobileLoadingElement.classList.remove('hidden')
    }
    
    // Add visual feedback to form
    this.element.style.opacity = '0.7'
    this.element.style.pointerEvents = 'none'
    
    // Add loading state to quick action buttons
    const quickActionButtons = document.querySelectorAll('[data-additive="true"]')
    quickActionButtons.forEach(button => {
      button.classList.add('opacity-50', 'pointer-events-none')
    })
  }

  hideLoading() {
    // Hide desktop loading indicator
    if (this.loadingElement) {
      this.loadingElement.classList.add('hidden')
    }
    
    // Hide mobile loading indicator  
    const mobileLoadingElement = document.querySelector(`${this.loadingTargetValue}-mobile`)
    if (mobileLoadingElement) {
      mobileLoadingElement.classList.add('hidden')
    }
    
    this.element.style.opacity = ''
    this.element.style.pointerEvents = ''
    this.isSubmitting = false
    
    // Remove loading state from quick action buttons
    const quickActionButtons = document.querySelectorAll('[data-additive="true"]')
    quickActionButtons.forEach(button => {
      button.classList.remove('opacity-50', 'pointer-events-none')
    })
  }

  // Handle keyboard shortcuts and navigation
  handleKeydown(event) {
    // Escape key clears all filters
    if (event.key === 'Escape') {
      event.preventDefault()
      this.clearAllFilters()
      return
    }

    // Ctrl+Enter or Cmd+Enter submits form immediately
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault()
      this.submit(event)
      return
    }

    // Alt+C clears search
    if (event.altKey && event.key === 'c') {
      event.preventDefault()
      this.clearSearch(event)
      return
    }
  }

  // Setup accessibility features
  setupAccessibility() {
    // Add ARIA labels and descriptions
    const searchInput = this.element.querySelector('input[name="search"]')
    if (searchInput) {
      searchInput.setAttribute('aria-describedby', 'search-help')
      
      // Create hidden help text
      if (!document.getElementById('search-help')) {
        const helpText = document.createElement('div')
        helpText.id = 'search-help'
        helpText.className = 'sr-only'
        helpText.textContent = 'Press Escape to clear all filters, Alt+C to clear search, or Ctrl+Enter to submit immediately'
        document.body.appendChild(helpText)
      }
    }

    // Add live region for filter feedback
    if (!document.getElementById('filter-status')) {
      const statusRegion = document.createElement('div')
      statusRegion.id = 'filter-status'
      statusRegion.className = 'sr-only'
      statusRegion.setAttribute('aria-live', 'polite')
      statusRegion.setAttribute('aria-atomic', 'true')
      document.body.appendChild(statusRegion)
    }
  }

  // Clear all filters keyboard shortcut
  clearAllFilters() {
    if (this.isSubmitting) return
    
    // Navigate to clean URL
    window.location.href = this.element.action
  }

  // Toggle auto-filtering on/off
  toggleAutoFilter(event) {
    this.autoFilterEnabled = !event.currentTarget.checked
    this.updateUIState()
    
    if (this.autoFilterEnabled) {
      this.announceFilterChange('Auto-filtering enabled')
    } else {
      this.announceFilterChange('Auto-filtering paused - use Apply Filters button')
    }
  }

  // Update UI state based on auto-filter setting
  updateUIState() {
    if (this.manualApplyBtn) {
      if (this.autoFilterEnabled) {
        this.manualApplyBtn.classList.add('hidden')
      } else {
        this.manualApplyBtn.classList.remove('hidden')
      }
    }
  }

  // Enhanced filter feedback with additive context
  announceFilterChange(message, isAdditive = false) {
    const statusRegion = document.getElementById('filter-status')
    if (statusRegion) {
      const enhancedMessage = isAdditive ? `Added filter: ${message}` : message
      statusRegion.textContent = enhancedMessage
      
      // Clear the message after announcement
      setTimeout(() => {
        statusRegion.textContent = ''
      }, 1000)
    }
    
    // Visual feedback for additive filters
    if (isAdditive) {
      this.showAdditiveFilterFeedback()
    }
  }

  // Show visual feedback when additive filters are applied
  showAdditiveFilterFeedback() {
    // Find the active filter container and briefly highlight it
    const activeFilterContainer = document.querySelector('.active-filter-container')
    if (activeFilterContainer) {
      activeFilterContainer.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50')
      
      setTimeout(() => {
        activeFilterContainer.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50')
      }, 1500)
    }
    
    // Add brief animation to quick action buttons to show they're additive
    const quickActionButtons = document.querySelectorAll('[data-additive="true"]')
    quickActionButtons.forEach(button => {
      button.classList.add('animate-pulse')
      setTimeout(() => {
        button.classList.remove('animate-pulse')
      }, 1000)
    })
  }

  // Handle additive dropdown submission
  handleAdditiveDropdownSubmit(selectElement) {
    const paramName = selectElement.name
    const newValue = selectElement.value
    
    // If empty value selected, treat as removal (normal form submit)
    if (!newValue) {
      this.element.submit()
      return
    }
    
    // Get current URL parameters
    const currentUrl = new URL(window.location)
    const currentParams = new URLSearchParams(currentUrl.search)
    
    // Get existing values for this parameter
    const existingValue = currentParams.get(paramName)
    let combinedValues
    
    if (existingValue) {
      // Split existing values (comma-separated) and add new value if not present
      const existingArray = existingValue.split(',').map(v => v.trim()).filter(v => v)
      if (!existingArray.includes(newValue)) {
        combinedValues = [...existingArray, newValue].join(',')
      } else {
        // Value already exists, keep existing
        combinedValues = existingValue
      }
    } else {
      combinedValues = newValue
    }
    
    // Update the parameter
    currentParams.set(paramName, combinedValues)
    
    // Remove page parameter to reset to first page
    currentParams.delete('page')
    
    // Navigate to the new URL
    const newUrl = `${currentUrl.pathname}?${currentParams.toString()}`
    window.location.href = newUrl
  }

  // Remove individual role from multi-role filter
  removeSingleRole(event) {
    event.preventDefault()
    if (this.isSubmitting) return
    
    const roleToRemove = event.currentTarget.dataset.roleValue
    this.removeSingleFilterValue('role', roleToRemove)
  }

  // Remove individual status from multi-status filter
  removeSingleStatus(event) {
    event.preventDefault()
    if (this.isSubmitting) return
    
    const statusToRemove = event.currentTarget.dataset.statusValue
    this.removeSingleFilterValue('status', statusToRemove)
  }

  // Remove individual industry from multi-industry filter
  removeSingleIndustry(event) {
    event.preventDefault()
    if (this.isSubmitting) return
    
    const industryToRemove = event.currentTarget.dataset.industryValue
    this.removeSingleFilterValue('industry', industryToRemove)
  }

  // Generic method to remove a single value from a comma-separated parameter
  removeSingleFilterValue(paramName, valueToRemove) {
    const currentUrl = new URL(window.location)
    const currentParams = new URLSearchParams(currentUrl.search)
    
    const existingValue = currentParams.get(paramName)
    if (!existingValue) return
    
    // Split existing values and remove the target value
    const values = existingValue.split(',').map(v => v.trim()).filter(v => v && v !== valueToRemove)
    
    if (values.length > 0) {
      currentParams.set(paramName, values.join(','))
    } else {
      currentParams.delete(paramName)
    }
    
    // Remove page parameter to reset to first page
    currentParams.delete('page')
    
    // Navigate to the new URL
    const newUrl = values.length > 0 || Array.from(currentParams.keys()).length > 0 
      ? `${currentUrl.pathname}?${currentParams.toString()}` 
      : currentUrl.pathname
    
    this.showLoading()
    window.location.href = newUrl
  }

  // Show error message to user
  showError(message) {
    // Create or update error notification
    let errorDiv = document.getElementById('filter-error')
    if (!errorDiv) {
      errorDiv = document.createElement('div')
      errorDiv.id = 'filter-error'
      errorDiv.className = 'fixed top-4 right-4 max-w-sm bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg shadow-lg z-50'
      document.body.appendChild(errorDiv)
    }

    errorDiv.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium">${message}</p>
        </div>
        <div class="ml-auto pl-3">
          <button type="button" class="inline-flex text-red-400 hover:text-red-600 focus:outline-none" onclick="this.parentElement.parentElement.parentElement.remove()">
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    `

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorDiv && errorDiv.parentNode) {
        errorDiv.remove()
      }
    }, 5000)

    // Announce error to screen readers
    this.announceFilterChange(`Error: ${message}`)
  }
}